import { useState } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'
import { CheckCircle2, Download, RefreshCw, Sparkles, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'

export function PWAUpdatePrompt() {
  const [isDownloading, setIsDownloading] = useState(false)

  const trackInstallingWorker = (worker: ServiceWorker) => {
    worker.addEventListener('statechange', () => {
      if (worker.state !== 'installing') {
        setIsDownloading(false)
      }
    })
  }

  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r: ServiceWorkerRegistration | undefined) {
      logger.info('Service Worker registered', {
        component: 'PWAUpdatePrompt',
        scope: r?.scope,
      })

      if (!r) return

      if (r.installing) {
        setIsDownloading(true)
        trackInstallingWorker(r.installing)
      }

      r.addEventListener('updatefound', () => {
        const newWorker = r.installing
        if (!newWorker) return
        setIsDownloading(true)
        trackInstallingWorker(newWorker)
      })
    },
    onRegisterError(error: Error) {
      logger.logError(
        error,
        LogCategory.ERROR,
        {
          component: 'PWAUpdatePrompt',
          action: 'sw_registration',
        }
      )
    },
  })

  const close = () => {
    setOfflineReady(false)
    setNeedRefresh(false)
    setIsDownloading(false)
  }

  const handleUpdate = () => {
    updateServiceWorker(true)
  }

  if (!offlineReady && !needRefresh && !isDownloading) {
    return null
  }

  const variant: 'downloading' | 'ready' | 'update' =
    isDownloading && !needRefresh && !offlineReady
      ? 'downloading'
      : needRefresh
        ? 'update'
        : 'ready'

  const meta = {
    downloading: {
      Icon: Download,
      iconClass: 'text-sky-600 dark:text-sky-400 bg-sky-500/10',
      title: 'Downloading update',
      description: 'A new version is being installed',
    },
    ready: {
      Icon: CheckCircle2,
      iconClass: 'text-emerald-600 dark:text-emerald-400 bg-emerald-500/10',
      title: 'Ready to work offline',
      description: 'Moly will keep working without a connection',
    },
    update: {
      Icon: Sparkles,
      iconClass: 'text-violet-600 dark:text-violet-400 bg-violet-500/10',
      title: 'New version available',
      description: 'Reload to get the latest improvements',
    },
  }[variant]

  return (
    <div
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      className={cn(
        'fixed bottom-4 right-4 z-50 w-[min(92vw,22rem)]',
        'rounded-2xl border border-border/60 bg-popover/95 text-popover-foreground',
        'shadow-[0_8px_24px_-8px_rgba(0,0,0,0.18),0_2px_6px_-2px_rgba(0,0,0,0.08)]',
        'backdrop-blur-md supports-[backdrop-filter]:bg-popover/80',
        'animate-in fade-in slide-in-from-bottom-4 duration-300',
      )}
    >
      <style>{`
        @keyframes pwa-indeterminate {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(200%); }
          100% { transform: translateX(-100%); }
        }
      `}</style>

      <div className="flex items-start gap-3 p-4">
        <div
          className={cn(
            'mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full',
            meta.iconClass,
          )}
          aria-hidden="true"
        >
          <meta.Icon className="size-4.5" strokeWidth={2.25} />
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold leading-5 tracking-tight">
            {meta.title}
          </h3>
          <p className="mt-0.5 text-[13px] leading-5 text-muted-foreground">
            {meta.description}
          </p>

          {variant === 'downloading' && (
            <div className="mt-3 h-1 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full w-1/3 rounded-full bg-sky-500 dark:bg-sky-400 animate-[pwa-indeterminate_1.5s_ease-in-out_infinite]"
              />
            </div>
          )}

          {variant !== 'downloading' && (
            <div className="mt-3 flex gap-2">
              {variant === 'update' && (
                <Button size="sm" onClick={handleUpdate} className="gap-1.5">
                  <RefreshCw className="size-3.5" />
                  Reload
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={close}>
                Dismiss
              </Button>
            </div>
          )}
        </div>

        <Button
          size="icon-sm"
          variant="ghost"
          onClick={close}
          aria-label="Close"
          className="-mr-1 -mt-1 size-7 rounded-full text-muted-foreground hover:text-foreground"
        >
          <X className="size-4" />
        </Button>
      </div>
    </div>
  )
}
